import { createApp, reactive } from 'vue';
import { guid } from '@/Utils';
import ErrorUtility from '@/Utils/ErrorUtility.js';
import { notificationTypes } from '@/Types/NotificatorTypes';
import Resources from '@/Resources';
import breakpoints from '@/Plugins/Breakpoints';

let notificator = null;
let areToastComponentsMounted = false;

function createElement(component, props, container = document.body) {
    const element = document.createElement('div');
    container.appendChild(element);

    const toastApp = createApp(component, props);

    toastApp.use(breakpoints);
    toastApp.provide('notificator', notificator);

    return toastApp.mount(element);
}

class Notificator extends Function {
    notifications = null;
    defaultProps = {
        duration: 4,
        persist: true,
    };

    constructor(initialProps) {
        super();
        const observable = reactive({ notifications: [] });
        this.notifications = observable.notifications;
        this.defaultProps = { ...this.defaultProps, ...initialProps };
        return new Proxy(this, {
            apply: (target, thisArg, args) => target.show(...args),
        });
    }

    show(props) {
        const id = guid();
        const title = ErrorUtility.getResponseErrorMessage(props.title);
        const model = { ...this.defaultProps, ...props, id, title };

        this.notifications.push(model);
        return props;
    }

    hide(notification) {
        const index = this.notifications.indexOf(notification);
        if (index > -1) {
            this.notifications.splice(index, 1);
        }
    }

    hideAll() {
        this.notifications.splice(0, this.notifications.length);
    }

    hideNotPersisted() {
        this.notifications.filter(n => !n.persist)
            .forEach(n => this.hide(n));
    }

    error(title, options = {}) {
        const type = notificationTypes.error;
        const duration = null;
        return this.show({ title, ...options, type, duration });
    }

    serverError(title, options = {}) {
        const description = Resources.Common.ServerErrorToastSubtitle;
        const type = notificationTypes.error;
        const duration = null;
        return this.show({ title, ...options, description, type, duration });
    }

    popupServerError(title, options = {}) {
        const description = Resources.Common.ServerErrorToastSubtitle;
        const type = notificationTypes.error;
        const duration = null;
        const persist = false;

        return this.show({
            title,
            description,
            type,
            duration,
            persist,
            ...options,
        });
    }

    info(title, options = {}) {
        const type = notificationTypes.info;
        return this.show({ title, ...options, type });
    }

    warning(title, options = {}) {
        const type = notificationTypes.warning;
        return this.show({ title, ...options, type });
    }

    success(title, options = {}) {
        const type = notificationTypes.success;
        return this.show({ title, ...options, type });
    }

    popupInfo(title, options = {}) {
        const type = notificationTypes.info;
        const duration = 10;
        return this.show({ title, ...options, type, duration });
    }

    popupSuccess(title, options = {}) {
        const type = notificationTypes.success;
        return this.show({ title, ...options, type });
    }
}

export default {
    install(app, options = {
        extensionName: '$notify',
        components: [],
    }) {
        notificator = notificator || new Notificator();
        app.config.globalProperties[options.extensionName] = notificator;

        const mountToastComponents = () => {
            if (areToastComponentsMounted) {
                return;
            }

            areToastComponentsMounted = true;
            options.components.forEach(c => createElement(c, options));
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', mountToastComponents, { once: true });
            return;
        }

        mountToastComponents();
    },
};
